import VueWrapper from '@/components/core/Vue/vue.wrapper';
import BaseFormComponent from '@/components/vuetify/Form/base-form';
import {AccountViewModel} from '@/sdk';
import {AccountsApi, AdminPlanApiService} from '@/sdk/api-services';
import {PlanModel, PlanSettings, PlanVersionRequestModel} from '@/sdk/models/payment/payment.models';
import {Component} from 'vue-property-decorator';

@Component
export default class PlansComponent extends VueWrapper {
    public planApi: AdminPlanApiService = new AdminPlanApiService();
    public accountsApi: AccountsApi = new AccountsApi();

    public $refs!: {
        form: BaseFormComponent;
    };

    public isLoading: boolean = false;
    public dialogTitle: string = 'Create Plan Version';
    public readonly dialogName: string = 'admin-plan-version-dialog';

    public selectedPlan: PlanModel | null = null;
    public plans: Array<PlanModel> = [];
    public accounts: Array<AccountViewModel> = [];

    public form: {
        code: string;
        name: string;
        priceUsd: number | null;
        periodDays: number | null;
        accountId: string | null;
        maxLocations: number | null;
        maxUsers: number | null;
        electricCircuitEnabled: boolean;
        electricCircuitPriceUsd: number | null;
        additionalUserPriceUsd: number | null;
    } = {
        code: '',
        name: '',
        priceUsd: null,
        periodDays: 30,
        accountId: null,
        maxLocations: 200,
        maxUsers: 5,
        electricCircuitEnabled: false,
        electricCircuitPriceUsd: 500,
        additionalUserPriceUsd: 20,
    };

    private readonly defaultFeatures: Record<string, boolean> = {
        siteSetup: true,
        teamSetup: true,
        jobSetup: true,
        reports: true,
        analysis: true,
    };

    public async mounted() {
        await Promise.all([this.loadPlans(), this.loadAccounts()]);
    }

    public async loadPlans() {
        this.isLoading = true;
        try {
            const filter = {
                where: {isActive: true},
                order: ['sortOrder ASC', 'code ASC', 'version DESC'],
                include: [{relation: 'account'}],
            };
            this.plans = (await this.planApi.getPlans(filter)) ?? [];
            console.log(this.plans);
        } catch (error) {
            this.AlertSrv.show('error', 'Unable to load plans.');
        } finally {
            this.isLoading = false;
        }
    }

    public async loadAccounts() {
        try {
            this.accounts = (await this.accountsApi.getAccounts()) ?? [];
        } catch (error) {
            this.accounts = [];
        }
    }

    public get accountOptions() {
        return this.accounts
            .filter(account => account.id)
            .map(account => ({
                value: account.id as string,
                text: account.accountTitle ?? account.accountType ?? account.id ?? '',
            }));
    }

    public get isPublicPlanVersion(): boolean {
        return !!this.selectedPlan?.isPublic;
    }

    public openCreateDialog() {
        this.dialogTitle = 'Create Plan Version';
        this.selectedPlan = null;
        this.resetForm();
        this.CoreSrv.OpenModal(this.dialogName);
    }

    public openNewVersionDialog(plan: PlanModel) {
        this.dialogTitle = `New Version: ${plan.code}`;
        this.selectedPlan = plan;
        this.form.code = plan.code;
        this.form.name = plan.name;
        this.form.priceUsd = plan.priceUsd ?? 0;
        this.form.periodDays = plan.periodDays ?? 30;
        this.form.accountId = plan.isPublic ? null : plan.accountId ?? null;
        const settings = (plan.settings ?? {}) as PlanSettings;
        this.form.maxLocations = settings.limits?.maxLocations ?? 0;
        this.form.maxUsers = settings.limits?.maxUsers ?? 0;
        this.form.electricCircuitEnabled = settings.addons?.electricCircuitModule?.enabled ?? false;
        this.form.electricCircuitPriceUsd = settings.addons?.electricCircuitModule?.priceUsd ?? 0;
        this.form.additionalUserPriceUsd = settings.pricing?.additionalUserPriceUsd ?? 0;
        this.CoreSrv.OpenModal(this.dialogName);
    }

    public closeDialog() {
        this.CoreSrv.CloseModal(this.dialogName);
    }

    public async deactivatePlan(plan: PlanModel) {
        const confirmed = await this.ConfirmSrv.open('Deactivate Plan', `Deactivate ${plan.code} v${plan.version}?`);
        if (!confirmed) return;

        try {
            this.LoaderSrv.showFullScreenLoader();
            await this.planApi.deactivatePlan(plan.id);
            this.AlertSrv.show('success', 'Plan deactivated.');
            await this.loadPlans();
        } catch (error: any) {
            this.AlertSrv.show('error', error?.message ?? 'Unable to deactivate plan.');
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    public async savePlanVersion() {
        try {
            const isValid = await this.$refs?.form?.validate();
            if (!isValid) return;

            const existingSettings = (this.selectedPlan?.settings ?? {}) as PlanSettings;
            const maxLocations = this.form.maxLocations === null || this.form.maxLocations === undefined ? undefined : Number(this.form.maxLocations);
            const maxUsers = this.form.maxUsers === null || this.form.maxUsers === undefined ? undefined : Number(this.form.maxUsers);
            const electricPrice =
                this.form.electricCircuitPriceUsd === null || this.form.electricCircuitPriceUsd === undefined ? 0 : Number(this.form.electricCircuitPriceUsd);
            const additionalUserPrice =
                this.form.additionalUserPriceUsd === null || this.form.additionalUserPriceUsd === undefined ? 0 : Number(this.form.additionalUserPriceUsd);

            const settings: PlanSettings = {
                ...existingSettings,
                limits: {
                    ...existingSettings.limits,
                    maxLocations,
                    maxUsers,
                },
                features: existingSettings.features ?? this.defaultFeatures,
                addons: {
                    ...existingSettings.addons,
                    electricCircuitModule: {
                        ...existingSettings.addons?.electricCircuitModule,
                        enabled: this.form.electricCircuitEnabled ?? false,
                        priceUsd: electricPrice,
                    },
                },
                pricing: {
                    ...existingSettings.pricing,
                    additionalUserPriceUsd: additionalUserPrice,
                },
            };
            const payload: PlanVersionRequestModel = {
                code: this.form.code,
                name: this.form.name,
                priceUsd: this.form.priceUsd ? Number(this.form.priceUsd) : 0,
                periodDays: this.form.periodDays ? Number(this.form.periodDays) : 30,
                isPublic: this.isPublicPlanVersion ? true : false,
                accountId: this.isPublicPlanVersion ? undefined : this.form.accountId ?? undefined,
                activate: true,
                settings,
            };

            this.LoaderSrv.showFullScreenLoader();
            await this.planApi.createPlanVersion(payload);
            this.AlertSrv.show('success', 'Plan version saved.');
            this.closeDialog();
            await this.loadPlans();
        } catch (error: any) {
            this.AlertSrv.show('error', error?.message ?? 'Unable to save plan version.');
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    public resetForm() {
        this.form = {
            code: '',
            name: '',
            priceUsd: null,
            periodDays: 30,
            accountId: null,
            maxLocations: 200,
            maxUsers: 5,
            electricCircuitEnabled: false,
            electricCircuitPriceUsd: 500,
            additionalUserPriceUsd: 20,
        };
        this.$refs?.form?.reset();
    }
}
